import { clamp } from "./math";

export type QualityLevel = "ECO" | "BALANCED" | "ULTRA";

export interface QualitySettings {
  readonly level: QualityLevel;
  readonly density: number;
  readonly pixelRatioCap: number;
}

const SETTINGS: Record<QualityLevel, QualitySettings> = {
  ECO: { level: "ECO", density: 0.5, pixelRatioCap: 1 },
  BALANCED: { level: "BALANCED", density: 0.75, pixelRatioCap: 1.25 },
  ULTRA: { level: "ULTRA", density: 1, pixelRatioCap: 1.5 },
};

export class QualityController {
  private level: QualityLevel;
  private lowFpsSeconds = 0;
  private highFpsSeconds = 0;

  constructor(initial: QualityLevel = "ULTRA") {
    this.level = initial;
  }

  get settings(): QualitySettings {
    return SETTINGS[this.level];
  }

  sample(fps: number, elapsedSeconds: number): QualitySettings | null {
    if (fps < 43) {
      this.lowFpsSeconds += elapsedSeconds;
      this.highFpsSeconds = 0;
    } else if (fps > 57) {
      this.highFpsSeconds += elapsedSeconds;
      this.lowFpsSeconds = 0;
    } else {
      this.lowFpsSeconds = clamp(this.lowFpsSeconds - elapsedSeconds, 0, 10);
      this.highFpsSeconds = clamp(this.highFpsSeconds - elapsedSeconds, 0, 20);
    }

    // Sample partitions such as 4 / 60 do not necessarily sum to exactly 4 in
    // binary floating point. Keep the time threshold independent of partitioning.
    if (this.lowFpsSeconds >= 4 - 1e-9) {
      this.lowFpsSeconds = 0;
      return this.lowerQuality();
    }
    if (this.highFpsSeconds >= 12 - 1e-9) {
      this.highFpsSeconds = 0;
      return this.raiseQuality();
    }
    return null;
  }

  force(level: QualityLevel): QualitySettings {
    this.level = level;
    this.lowFpsSeconds = 0;
    this.highFpsSeconds = 0;
    return this.settings;
  }

  private lowerQuality(): QualitySettings | null {
    const next: QualityLevel = this.level === "ULTRA" ? "BALANCED" : "ECO";
    if (next === this.level) {
      return null;
    }
    this.level = next;
    return this.settings;
  }

  private raiseQuality(): QualitySettings | null {
    const next: QualityLevel = this.level === "ECO" ? "BALANCED" : "ULTRA";
    if (next === this.level) {
      return null;
    }
    this.level = next;
    return this.settings;
  }
}
